// Systemic Formulas "Holobiome Roadmap" — the five sequential kits (the 5 P's)
// and the products in each. Source of truth is the "SF-Supps" tab of the
// LabSmarts patterns sheet (gid 586166022), whose per-product "Holobiome Kit"
// column carries the membership. The kit X-matrix that used to sit at column N
// is gone from that tab as of the 2026-08-27 export, which is one more reason
// this is encoded here: the Supplement Protocols panel groups recommended
// supplements by kit without a runtime sheet fetch.
//
// productNames use the master supplement TITLE exactly as
// FurtherConsiderations.resolveSupplement() returns it (e.g. "Immune Superfood",
// "Clear 1 µBiomic"), so an active-supplement match is a plain name compare that
// unifies a product's primary and "-2" tag variants (both resolve to the same
// title).
//
// Not every product has a pattern pointing at it yet. Measured against the live
// FurtherConsiderations table on 2026-08-31, eight of the twenty-one have a
// catalog row but no pattern row anywhere: MucosaGenic, BindGenic, Terra
// µBiomic, Terra Superfood, Immune µBiomic, Immune Superfood, Neuro µBiomic and
// Neuro Superfood. (The Terra and Clear products are authored in the Digestion
// patterns, which are not in the table yet.)
//
// The panel does NOT single any of them out. It used to mark two — the Neuro
// pair — as "not read by blood work", which was a claim about the products when
// the truth is an authoring backlog, and it was two of eight. They all render as
// ordinary kit rows now, and a kit's coverage counts every product in it.

import { Section } from './Section';

// The brand the roadmap belongs to. EVERYTHING Holobiome is scoped to it — the
// kits, the product table under them, and the report section that holds both —
// so unchecking "Systemic Formulas" in Supplement Settings takes the whole
// section away. Lives here rather than in either panel so the two can't drift.
export const SYSTEMIC_FORMULAS_BRAND = 'systemicFormulas';

// The Holobiome section's "Include in PDF" keys — ONE PER HALF. Pseudo-sections,
// the way MVX_SECTION is in bloodwork-test-analysis.tsx: neither is a body
// system, they just need stable strings in excludedFromPdfSections. They get
// their own keys rather than sharing Section.SUPPLEMENTS because the default
// profile enables Systemic Formulas ONLY — with no second brand checked the
// Supplements panel renders nothing, and its checkbox (the one that would have
// controlled the roadmap too) wouldn't be on screen to uncheck.
//
// THREE keys, because the section prints as three independent documents: the
// shortlist, which is what to start with; the pattern-identified table, which
// is everything this test called for; and the month spine, which is the
// program. A practitioner sending a client the products they need without the
// five-month protocol (or just the shortlist, or the protocol without the
// shopping list) was previously an all-or-nothing choice.
//
// Splitting a key that used to mean "the whole section" was safe because at the
// time none of them was persisted: Report Settings listed no Holobiome key, so
// no profile could be carrying the old one.
//
// They ARE persisted now. Report Settings > PDF Inclusion gained a row per view
// on 2026-09-23 (shown only while Systemic Formulas is checked), so these three
// strings are stored in UserProfile.excludedFromPdfSections and renaming one
// now silently re-includes the view for anyone who had turned it off — the same
// data migration a Section rename is. See LEGACY_SECTION_NAMES in Section.ts.
export const HOLOBIOME_SECTION = 'Holobiome Roadmap' as unknown as Section;
export const HOLOBIOME_SYSTEMS_SECTION =
  'Holobiome By System' as unknown as Section;
export const HOLOBIOME_TOP_SECTION =
  'Holobiome Top Priority' as unknown as Section;

export type HolobiomeKitKey =
  | 'prepare'
  | 'purify'
  | 'promote'
  | 'protect'
  | 'preserve';

export interface HolobiomeKit {
  key: HolobiomeKitKey;
  // Roadmap order (1-based). The kits are a sequential monthly protocol, so this
  // doubles as the "Month N" in the card title (Prepare → Preserve).
  month: number;
  name: string;
  // Short descriptor shown under the kit name (e.g. the Prepare card reads
  // "Prepare" / "Drainage & Energy"). '' renders no subtitle.
  subtitle: string;
  // The kit's hue in MONTH_PALETTE below — identity, not status.
  color: string;
  productNames: string[];
}

// THE MONTH PALETTE.
//
// Five categorical hues, one per kit — orange, blue, bronze, violet, green.
// Not a ramp and not a brand set: this is an IDENTITY encoding (which month am
// I looking at), and identity wants distinct hues, not depths of one.
//
// Derived rather than picked. Hand-chosen "tasteful" sets kept collapsing under
// color-blind simulation — a teal/magenta pair that looked obviously different
// measured ΔE 1.0 under deuteranopia. These were generated in OKLCH at a shared
// lightness (L 0.54) with each hue pushed to its maximum in-gamut chroma, then
// searched for the ordering with the best worst-adjacent separation, then
// validated. They clear every gate:
//
//   worst adjacent CVD ΔE 23.6 (protan) against a target of 8
//   worst adjacent normal-vision ΔE 25.0 against a floor of 15
//   every hue >= 4.5:1 on white, so it can carry the 15px bold kit title
//   every 0.82 tint stays tellable from the #f7f8f9 of an unhighlighted row
//
// Red is deliberately absent: in a blood-work report red is a status color
// (alarm / priority) and must never double as an identity hue.
//
// The mapping is also apt rather than arbitrary — orange for Drainage & Energy,
// blue for Detox, bronze for Immunity, violet for Neurological, green for
// Maintenance.
const MONTH_PALETTE = {
  orange: '#b04d00',
  blue:   '#0072be',
  bronze: '#836d00',
  violet: '#8347cf',
  green:  '#008622',
};

// The panel's own accent — the LabSmarts blue from the card header this section
// sits in (index.css). Deliberately NOT a month hue and NOT a partner color:
// the call-to-action is a LabSmarts control, and giving it its own blue put two
// near-identical blues on screen (1.06:1 apart, which reads as a mistake).
export const HOLOBIOME_ANCHOR = '#337ab7';

export const HOLOBIOME_KITS: HolobiomeKit[] = [
  {
    key: 'prepare',
    month: 1,
    name: 'Prepare',
    subtitle: 'Drainage & Energy',
    color: MONTH_PALETTE.orange,
    productNames: ['EnerGenic', 'MethylGenic', 'ImmuneGenic', 'LymphGenic', 'MucosaGenic'],
  },
  {
    key: 'purify',
    month: 2,
    name: 'Purify',
    subtitle: 'Detox & Gut Health',
    color: MONTH_PALETTE.blue,
    productNames: ['Clear 1 µBiomic', 'Clear 2 µBiomic', 'Terra µBiomic', 'Terra Superfood', 'DetoxiGenic', 'BindGenic'],
  },
  {
    key: 'promote',
    month: 3,
    name: 'Promote',
    subtitle: 'Immunity & Microbiome',
    color: MONTH_PALETTE.bronze,
    productNames: ['ImmuneGenic', 'MucosaGenic', 'Immune µBiomic', 'Immune Superfood', 'CellGenic'],
  },
  {
    key: 'protect',
    month: 4,
    name: 'Protect',
    subtitle: 'Neurological Health',
    color: MONTH_PALETTE.violet,
    productNames: ['Neuro µBiomic', 'NeuroGenic', 'Neuro Superfood', 'CalmGenic', 'EpiGenic'],
  },
  {
    key: 'preserve',
    month: 5,
    name: 'Preserve',
    subtitle: 'Maintenance',
    color: MONTH_PALETTE.green,
    productNames: ['Terra µBiomic', 'Terra Superfood', 'CellGenic', 'VitaGenic', 'OrganGenic'],
  },
];

// FALLBACK dosing by form, from the original Systemic Formulas protocol sheet.
//
// The catalog is the source of a product's serving now: the SF-Supps tab's
// "Maintenance Dose" column, transcribed from the Practitioner Clinical Guide
// (9.14.26), reaches the cat 500xxx rows as `dose` and servingForProduct below
// prints it. Checked against that guide, the by-form guess here was right for
// 37 of 146 products and wrong for 17 of the 20 Holobiome kit products (CAL is
// 5 capsules a day, OrganGenic 5, EnerGenic 2 with breakfast, BindGenic 2 at
// bedtime away from everything else), so it is kept only for rows the sheet
// has not filled: the kits, a discontinued product, a second brand.
//
// The first two rows are by FORM — every capsule, every powder — so they hold
// whatever the recommended protocol turns out to be, and always print. The last
// two name ONE product each, and print only when a pattern actually pointed to
// that product: a serving size for something this test never recommended is a
// line the practitioner has to read past to reach the ones that apply.
// `onlyWhenRecommended` is the catalog title, matched through
// normalizeSupplementName so a hyphen or a capital can't miss it.
export const HOLOBIOME_DOSING: {
  product: string;
  serving: string;
  onlyWhenRecommended?: string;
}[] = [
  { product: 'All capsule products', serving: '1 capsule twice daily with meals' },
  {
    product: 'Superfood (powder)',
    serving: '1 scoop daily, mixed in 8–12 oz liquid',
  },
  {
    product: 'ALG LQ (liquid)',
    serving: '1 mL twice daily',
    onlyWhenRecommended: 'ALG LQ',
  },
  {
    product: 'Metabo-Shake',
    serving: '2 scoops daily, or 4 scoops as a full meal replacement',
    onlyWhenRecommended: 'Metabo-Shake',
  },
];

// The serving the client list prints beside a product.
//
// The catalog row's own `dose` wins: it is the sheet's Maintenance Dose cell,
// the guide's wording verbatim. Only a row with no dose (or no row at all, when
// a caller has already decided the product deserves a guess) falls through to
// doseForProduct. Trimmed, so a cell holding a stray space is "not filled in"
// rather than a blank serving on the client's page.
export const servingForProduct = (
  name: string,
  row?: { dose?: string } | null,
): string => (row?.dose || '').trim() || doseForProduct(name);

// The FALLBACK serving for ONE product, resolved off HOLOBIOME_DOSING by its
// form — reached only through servingForProduct for a catalog row the sheet
// has not dosed yet.
//
// A guess, and knowingly so: it reads the form off the NAME. A row that names
// the product wins; anything called "Superfood" is the powder; everything else
// is a capsule. A tincture or liquid that reaches here prints as a capsule,
// which is exactly why the sheet now carries the dose and this is no longer the
// first thing asked.
export const doseForProduct = (name: string): string => {
  const key = normalizeSupplementName(name);
  const named = HOLOBIOME_DOSING.find(
    row =>
      row.onlyWhenRecommended &&
      normalizeSupplementName(row.onlyWhenRecommended) === key,
  );
  if (named) return named.serving;
  // BY FORM, off the name. The shake test is a pattern rather than the exact
  // "Metabo-Shake" above because the catalog carries it by FLAVOR — Metabo-Shake
  // Vanilla and Metabo-Shake Chocolate — and the two unflavored rows that both
  // pointed at the chocolate page are being removed from the sheet (2026-09-02).
  // Without this, both flavors would fall through to the capsule line and print
  // "1 capsule twice daily with meals" for a powder, on the client's own page.
  // The hyphen is optional: the sheet has spelled it both ways.
  const form = /metabo[\s-]*shake/.test(key)
    ? 'Metabo-Shake'
    : key.includes('superfood')
      ? 'Superfood (powder)'
      : 'All capsule products';
  return HOLOBIOME_DOSING.find(row => row.product === form)!.serving;
};

// Normalize a supplement name for matching (case/whitespace-insensitive). The
// micro sign (µ, U+00B5) and Greek mu (μ, U+03BC) both appear in the wild for
// "µBiomic"; fold them so a product name always matches its recommendation.
export const normalizeSupplementName = (name: string): string =>
  (name || '')
    .trim()
    .toLowerCase()
    .replace(/μ/g, 'µ')
    .replace(/\s+/g, ' ');

// Products that ship in more than one kit are NOT marked as such.
//
// ImmuneGenic and MucosaGenic are in Months 1 and 3, Terra µBiomic and Terra
// Superfood in 2 and 5, CellGenic in 3 and 5. The panel used to label these
// "also Month N", on the theory that an unlabeled repeat reads as a data bug.
// It doesn't: each month arrives as its own packet holding that month's
// supplements, so a product in two kits is simply in both packets, and there
// is nothing for the reader to reconcile. The label invited the question it was
// meant to answer, so there is no helper here to compute it.
